import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, TrendingUp, DollarSign, BarChart3, MapPin, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Load stats
      const statsRes = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Load users
      const usersRes = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(usersRes.data.users || []);

      // Load analyses
      const analysesRes = await axios.get(`${API}/admin/analyses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyses(analysesRes.data.analyses || []);

      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
      if (error.response?.status === 403) {
        navigate('/');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Admin Dashboard
            </h1>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            Back to App
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-sm font-medium ${darkMode ? 'text-gray-400' : ''}`}>
                Total Users
              </CardTitle>
              <Users className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>
                {stats?.total_users || 0}
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-sm font-medium ${darkMode ? 'text-gray-400' : ''}`}>
                Total Analyses
              </CardTitle>
              <BarChart3 className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>
                {stats?.total_analyses || 0}
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-sm font-medium ${darkMode ? 'text-gray-400' : ''}`}>
                Revenue
              </CardTitle>
              <DollarSign className="w-4 h-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>
                ${(stats?.total_revenue || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-sm font-medium ${darkMode ? 'text-gray-400' : ''}`}>
                Active Subscriptions
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>
                {stats?.active_subscriptions || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className={darkMode ? 'bg-gray-800' : ''}>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="analyses">Analyses</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="map">World Map</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={darkMode ? 'text-white' : ''}>User List</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-gray-700' : ''}`}>
                        <th className={`text-left p-2 ${darkMode ? 'text-gray-400' : ''}`}>Email</th>
                        <th className={`text-left p-2 ${darkMode ? 'text-gray-400' : ''}`}>Credits</th>
                        <th className={`text-left p-2 ${darkMode ? 'text-gray-400' : ''}`}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice(0, 20).map((user, idx) => (
                        <tr key={idx} className={`border-b ${darkMode ? 'border-gray-700' : ''}`}>
                          <td className={`p-2 ${darkMode ? 'text-gray-300' : ''}`}>{user.email}</td>
                          <td className={`p-2 ${darkMode ? 'text-gray-300' : ''}`}>{user.credits || 0}</td>
                          <td className={`p-2 ${darkMode ? 'text-gray-300' : ''}`}>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analyses" className="space-y-4">
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={darkMode ? 'text-white' : ''}>Recent Analyses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analyses.slice(0, 10).map((analysis, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                          {analysis.content_type}
                        </span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {analysis.timestamp ? new Date(analysis.timestamp).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-4">
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={darkMode ? 'text-white' : ''}>Revenue Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Revenue analytics coming soon...
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="map" className="space-y-4">
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={darkMode ? 'text-white' : ''}>User World Map</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <MapPin className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>World map visualization coming soon...</p>
                  <p className="text-sm mt-2">Will show user locations and activity by region</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
